import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Message, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';
import { StyleClassModule } from "primeng/styleclass";
import { InputOtpModule } from 'primeng/inputotp';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { PasswordModule } from 'primeng/password';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/account/auth.service';
import { UtilityService } from '../../services/utility/utility.service';
import { validateEmailResponse } from '../../models/account/validateEmailResponse';
import { ValidatePhoneRequest } from '../../models/account/validatePhoneRequest';
import { ValidatePhoneResponse } from '../../models/account/validatePhoneResponse';
import { verifyOTPRequest } from '../../models/account/verifyOTPRequest';
import { VerifyOTPResponse } from '../../models/account/verifyOTPResponse';
import { VerifyPhoneOTPRequest } from '../../models/account/verifyPhoneOTPRequest';
import { VerifyPhoneOTPResponse } from '../../models/account/verifyPhoneOTPResponse';
import { Router, RouterModule, NavigationEnd, ActivatedRoute } from '@angular/router';
import { loginResponse } from '../../models/account/loginResponse';
import { CreateAccountRequest } from '../../models/account/createAccountRequest';
import { DividerModule } from 'primeng/divider';
import { SocialLoginComponent } from "../social-login/social-login.component";
import { filter } from 'rxjs/operators';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

interface slider {
  image: string,
  heading: string,
  description: string
}

interface HearAboutUsOption {
  label: string;
  value: string;
  icon: string;
  id: string;
}

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    ButtonModule,
    CarouselModule,
    StyleClassModule,
    InputOtpModule,
    MessagesModule,
    MultiSelectModule,
    InputTextModule,
    DropdownModule,
    PasswordModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    RouterModule,
    DividerModule,
    SocialLoginComponent,
    InputGroupModule,
    InputGroupAddonModule
  ],
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.scss'
})
export class SignUpComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private utilityService = inject(UtilityService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private source?: string | null = null;
  private sourceUrl?: string | null = null;
  signupForm!: FormGroup;
  messages: Message[] = [];

  private socialSignUpState: any;
  private routerSubscription: any;
  constructor() {
    const navigation = this.router.getCurrentNavigation();
    this.socialSignUpState = navigation?.extras.state as { userId: string, email: string, emailVerified: boolean };
  }

  // Social auth state
  isSocialAuth = false;

  // Form states
  currentStep = 1;
  emailValidated = false;
  phoneValidated = false;
  emailOTPSent = false;
  phoneOTPSent = false;
  emailOTPVerified = false;
  phoneOTPVerified = false;
  isEmailLoading = false;
  isEmailOtpLoading = false;
  isPhoneLoading = false;
  isPhoneOtpLoading = false;
  isAccountCreating = false;

  // OTP Timer states
  emailOTPTimer = 0;
  phoneOTPTimer = 0;
  emailOTPTimerInterval: any;
  phoneOTPTimerInterval: any;
  emailCanResend = false;
  phoneCanResend = false;

  // Phone input configuration
  selectedCountry: any = null;
  phoneNumber: string = '';
  maxPhoneLength = 15;

  // Country codes data
  countryOptions: any[] = [];

  // Hear about us options from SignUpSteps component
  hearAboutUsOptions: HearAboutUsOption[] = [
    { label: 'Instagram', value: 'Instagram', icon: '../../../assets/icons/instagram.png', id: 'insta' },
    { label: 'YouTube', value: 'YouTube', icon: '../../../assets/icons/youtube.png', id: 'youtube' },
    { label: 'School', value: 'School', icon: '../../../assets/icons/school.png', id: 'sch' },
    { label: 'Google Search', value: 'Google Search', icon: '../../../assets/icons/google.png', id: 'gs' },
    { label: 'Word of mouth', value: 'Word of mouth', icon: '../../../assets/icons/word-of-mouth.png', id: 'wom' },
    { label: 'Other', value: 'Other', icon: '../../../assets/icons/other.png', id: 'other' },
  ];

  sliderData: slider[] = [
    {
      image: "../../../assets/images/login-slide-1.png",
      heading: "Ask Me Anything",
      description: "Clear your doubts with our state-of-the-art Co-pilot !"
    },
    {
      image: "../../../assets/images/login-slide-2.png",
      heading: "Find the University that fits you best",
      description: "Explore a wide range  of universities and their offerings"
    },
    {
      image: "../../../assets/images/login-slide-3.png",
      heading: "Connect with Mentors",
      description: "Reach your goals faster by scheduling sessions with people who've been down the same road"
    }
  ];

  responsiveOptions: any[] | undefined;

  ngOnInit() {
    // Capture source/utm_source from query params for attribution
    this.route.queryParamMap.subscribe(params => {
      this.source = params.get('src') || params.get('utm_source');
      if (this.source) {
        sessionStorage.setItem('signup_source', this.source);
        console.log('Signup source detected:', this.source);
        this.sourceUrl = window.location.href;
        sessionStorage.setItem('signup_source_full_url', this.sourceUrl);
      }
    });

    this.initializeForm();
    this.applySocialAuthState();

    this.initializeCountryData();
    this.responsiveOptions = [
      {
        breakpoint: '1024px',
        numVisible: 1,
        numScroll: 1
      },
      {
        breakpoint: '768px',
        numVisible: 1,
        numScroll: 1
      },
      {
        breakpoint: '560px',
        numVisible: 1,
        numScroll: 1
      }
    ];
  }

  private applySocialAuthState() {
    // Apply social sign-up state if available
    if (this.socialSignUpState?.emailVerified) {
      this.emailValidated = true;
      this.emailOTPSent = true;
      this.emailOTPVerified = true;
      this.isSocialAuth = true;
      this.currentStep = 3;

      // Update form with social auth data
      this.signupForm.patchValue({
        email: this.socialSignUpState.email
      });

      this.signupForm.get('email')?.disable();

      // Clear any existing messages
      this.messages = [];

      // Show success message
      // this.showMessage('success', 'Email verified via social authentication');
    }
  }

  private initializeForm() {
    this.signupForm = this.fb.group({
      email: [this.socialSignUpState?.email ?? '', [Validators.required, Validators.email]],
      emailOTP: [''],
      countryCode: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required, this.phoneNumberValidator]],
      phoneOTP: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      hearAboutUs: [[], [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  private initializeCountryData() {
    // Use comprehensive country list with flags and calling codes
    this.countryOptions = [
      { name: 'Afghanistan', code: 'AF', callingCode: '+93', flag: '🇦🇫' },
      { name: 'Albania', code: 'AL', callingCode: '+355', flag: '🇦🇱' },
      { name: 'Algeria', code: 'DZ', callingCode: '+213', flag: '🇩🇿' },
      { name: 'Argentina', code: 'AR', callingCode: '+54', flag: '🇦🇷' },
      { name: 'Australia', code: 'AU', callingCode: '+61', flag: '🇦🇺' },
      { name: 'Austria', code: 'AT', callingCode: '+43', flag: '🇦🇹' },
      { name: 'Bangladesh', code: 'BD', callingCode: '+880', flag: '🇧🇩' },
      { name: 'Belgium', code: 'BE', callingCode: '+32', flag: '🇧🇪' },
      { name: 'Brazil', code: 'BR', callingCode: '+55', flag: '🇧🇷' },
      { name: 'Canada', code: 'CA', callingCode: '+1', flag: '🇨🇦' },
      { name: 'China', code: 'CN', callingCode: '+86', flag: '🇨🇳' },
      { name: 'Denmark', code: 'DK', callingCode: '+45', flag: '🇩🇰' },
      { name: 'Egypt', code: 'EG', callingCode: '+20', flag: '🇪🇬' },
      { name: 'Finland', code: 'FI', callingCode: '+358', flag: '🇫🇮' },
      { name: 'France', code: 'FR', callingCode: '+33', flag: '🇫🇷' },
      { name: 'Germany', code: 'DE', callingCode: '+49', flag: '🇩🇪' },
      { name: 'India', code: 'IN', callingCode: '+91', flag: '🇮🇳' },
      { name: 'Indonesia', code: 'ID', callingCode: '+62', flag: '🇮🇩' },
      { name: 'Ireland', code: 'IE', callingCode: '+353', flag: '🇮🇪' },
      { name: 'Italy', code: 'IT', callingCode: '+39', flag: '🇮🇹' },
      { name: 'Japan', code: 'JP', callingCode: '+81', flag: '🇯🇵' },
      { name: 'Malaysia', code: 'MY', callingCode: '+60', flag: '🇲🇾' },
      { name: 'Mexico', code: 'MX', callingCode: '+52', flag: '🇲🇽' },
      { name: 'Netherlands', code: 'NL', callingCode: '+31', flag: '🇳🇱' },
      { name: 'New Zealand', code: 'NZ', callingCode: '+64', flag: '🇳🇿' },
      { name: 'Nigeria', code: 'NG', callingCode: '+234', flag: '🇳🇬' },
      { name: 'Norway', code: 'NO', callingCode: '+47', flag: '🇳🇴' },
      { name: 'Pakistan', code: 'PK', callingCode: '+92', flag: '🇵🇰' },
      { name: 'Philippines', code: 'PH', callingCode: '+63', flag: '🇵🇭' },
      { name: 'Poland', code: 'PL', callingCode: '+48', flag: '🇵🇱' },
      { name: 'Portugal', code: 'PT', callingCode: '+351', flag: '🇵🇹' },
      { name: 'Russia', code: 'RU', callingCode: '+7', flag: '🇷🇺' },
      { name: 'Singapore', code: 'SG', callingCode: '+65', flag: '🇸🇬' },
      { name: 'South Africa', code: 'ZA', callingCode: '+27', flag: '🇿🇦' },
      { name: 'South Korea', code: 'KR', callingCode: '+82', flag: '🇰🇷' },
      { name: 'Spain', code: 'ES', callingCode: '+34', flag: '🇪🇸' },
      { name: 'Sweden', code: 'SE', callingCode: '+46', flag: '🇸🇪' },
      { name: 'Switzerland', code: 'CH', callingCode: '+41', flag: '🇨🇭' },
      { name: 'Thailand', code: 'TH', callingCode: '+66', flag: '🇹🇭' },
      { name: 'Turkey', code: 'TR', callingCode: '+90', flag: '🇹🇷' },
      { name: 'United Arab Emirates', code: 'AE', callingCode: '+971', flag: '🇦🇪' },
      { name: 'United Kingdom', code: 'GB', callingCode: '+44', flag: '🇬🇧' },
      { name: 'United States', code: 'US', callingCode: '+1', flag: '🇺🇸' },
      { name: 'Vietnam', code: 'VN', callingCode: '+84', flag: '🇻🇳' }
    ];

    // Set default country to India
    this.selectedCountry = this.countryOptions.find(country => country.code === 'IN');
    if (this.selectedCountry) {
      this.signupForm.get('countryCode')?.setValue(this.selectedCountry);
    }

    console.log('Country data loaded:', this.countryOptions.length, 'countries');
  }

  private passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  private phoneNumberValidator = (control: any) => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const phoneNumber = control.value.toString().trim();

    // Check if phone number is exactly 10 digits
    if (phoneNumber.length !== 10) {
      return { invalidPhoneLength: true };
    }

    // Check if phone number contains only numeric characters
    if (!/^[0-9]+$/.test(phoneNumber)) {
      return { invalidPhoneFormat: true };
    }

    return null; // Valid phone number
  }

  validateEmail() {
    if (this.signupForm.get('email')?.invalid) {
      this.showMessage('error', 'Please enter a valid email address');
      return;
    }

    this.isEmailLoading = true;
    const email = this.signupForm.get('email')?.value;

    this.authService.validateEmail(email).subscribe({
      next: (response: validateEmailResponse) => {
        this.isEmailLoading = false;
        this.emailValidated = true;
        this.emailOTPSent = response.isEmailSent;

        if (response.isAccountExists) {
          this.emailValidated = false;
          this.emailOTPSent = false;
          this.showMessage('warn', 'Email already exists. Please login to continue');
        } else {
          this.showMessage('success', 'OTP sent to your email for verification.');
          this.startEmailOTPTimer();
          this.nextStep(); // Move to OTP step
        }
      },
      error: (error) => {
        this.isEmailLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message
        });
      }
    });
  }

  verifyEmailOTP() {
    const email = this.signupForm.get('email')?.value;
    const otp = this.signupForm.get('emailOTP')?.value;

    if (!email || !otp) {
      this.showMessage('error', 'Please enter email and OTP');
      return;
    }

    this.isEmailOtpLoading = true;
    const request: verifyOTPRequest = {
      email: email,
      oTP: otp
    };

    this.authService.verifyOTP(request).subscribe({
      next: (response: VerifyOTPResponse) => {
        this.isEmailOtpLoading = false;
        this.emailOTPVerified = true;
        this.stopEmailOTPTimer();
        // Disable the email form control to prevent editing
        this.signupForm.get('email')?.disable();
        // Clear any existing messages
        this.messages = [];
        this.showMessage('success', 'Email verified successfully!');
        this.nextStep(); // Move to Mobile step
      },
      error: (error) => {
        this.isEmailOtpLoading = false;
        this.showMessage('error', 'Invalid OTP. Please try again.');
        console.error('Email OTP verification error:', error);
      }
    });
  }

  // Commented out phone validation method - temporary until SMS gateway integration
  validatePhone() {
    const countryCode = this.signupForm.get('countryCode')?.value;
    const phoneNumber = this.signupForm.get('phoneNumber')?.value;

    if (!countryCode || !phoneNumber) {
      this.showMessage('error', 'Please select country and enter phone number');
      return;
    }

    if (this.signupForm.get('phoneNumber')?.invalid) {
      this.showMessage('error', 'Please enter a valid phone number');
      return;
    }

    this.isPhoneLoading = true;
    const fullPhoneNumber = phoneNumber;

    const request: ValidatePhoneRequest = {
      phoneNumber: fullPhoneNumber
    };

    this.authService.validatePhone(request).subscribe({
      next: (response: ValidatePhoneResponse) => {
        this.isPhoneLoading = false;
        this.phoneValidated = true;
        this.phoneOTPSent = response.isOTPSent;

        if (response.isAccountExists) {
          this.phoneValidated = false;
          this.phoneOTPSent = false;
          this.showMessage('warn', 'Phone number already exists. Please login to continue');
          return;
        } else {
          this.showMessage('success', 'OTP sent to your phone for verification.You can resend after 27 seconds.');
          this.startPhoneOTPTimer();
          this.nextStep(); // Move to Phone OTP step
        }
      },
      error: (error) => {
        this.isPhoneLoading = false;
        this.showMessage('error', 'Failed to validate phone number. Please try again.');
        console.error('Phone validation error:', error);
      }
    });
  }

  onCountryChange(country: any) {
    this.selectedCountry = country;
    console.log('Country changed:', country);
  }

  onPhoneNumberInput(event: any) {
    const phoneNumber = event.target.value;
    this.phoneNumber = phoneNumber;

    // Let the custom validator handle validation
    // Don't manually set errors as it interferes with the validator
    console.log('Phone number changed:', phoneNumber);
  }

  // Commented out phone OTP verification method - temporary until SMS gateway integration
  verifyPhoneOTP() {
    const countryCode = this.signupForm.get('countryCode')?.value;
    const phoneNumber = this.signupForm.get('phoneNumber')?.value;
    const otp = this.signupForm.get('phoneOTP')?.value;

    if (!countryCode || !phoneNumber || !otp) {
      this.showMessage('error', 'Please enter phone number and OTP');
      return;
    }

    this.isPhoneOtpLoading = true;
    const fullPhoneNumber = phoneNumber;

    const request: VerifyPhoneOTPRequest = {
      phoneNumber: fullPhoneNumber,
      oTP: otp
    };

    this.authService.verifyPhoneOTP(request).subscribe({
      next: (response: VerifyPhoneOTPResponse) => {
        this.isPhoneOtpLoading = false;
        this.phoneOTPVerified = true;
        this.stopPhoneOTPTimer();
        // Disable the phone form controls to prevent editing
        this.signupForm.get('countryCode')?.disable();
        this.signupForm.get('phoneNumber')?.disable();
        // Clear any existing messages
        this.messages = [];
        this.showMessage('success', 'Mobile number verified successfully!');
        this.nextStep(); // Move to Password step
      },
      error: (error) => {
        this.isPhoneOtpLoading = false;
        this.showMessage('error', 'Invalid OTP. Please try again.');
        console.error('Phone OTP verification error:', error);
      }
    });
  }

  onCreateAccount() {
    if (this.signupForm.invalid) {
      this.showMessage('error', 'Please fill all required fields correctly');
      return;
    }

    // Updated validation - only require email verification, not phone verification
    if (!this.emailOTPVerified) {
      this.showMessage('error', 'Please verify your email first');
      return;
    }
    this.isAccountCreating = true;
    const request: CreateAccountRequest = {
      email: this.signupForm.get('email')?.value,
      phoneNumber: this.signupForm.get('phoneNumber')?.value,
      password: this.signupForm.get('password')?.value,
      confirmPassword: this.signupForm.get('confirmPassword')?.value,
      hearAboutUs: this.signupForm.get('hearAboutUs')?.value as string[],
      source: this.source ?? undefined,
      sourceUrl: this.sourceUrl ?? undefined
    };

    if (this.socialSignUpState == null) {
      this.authService.createAccount(request).subscribe({
        next: (response: loginResponse) => {
          this.isAccountCreating = false;
          localStorage.setItem("account", JSON.stringify(response));
          this.router.navigate(['/signup-thank-you']);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Account created successfully!'
          });
        },
        error: (error: any) => {
          this.isAccountCreating = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message
          });
        }
      });
    }
    else {
      // ensure source is included for update flow as well
      request.source = request.source ?? this.source ?? sessionStorage.getItem('signup_source') ?? undefined;
      request.sourceUrl = request.sourceUrl ?? this.sourceUrl ?? sessionStorage.getItem('signup_source_full_url') ?? undefined;
      this.authService.updateAccount(this.socialSignUpState.userId, request).subscribe({
        next: (response: loginResponse) => {
          this.isAccountCreating = false;
          localStorage.setItem("account", JSON.stringify(response));
          this.router.navigate(['/signup-thank-you']);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Account created successfully!'
          });
        },
        error: (error: any) => {
          this.isAccountCreating = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message
          });
        }
      });
    }
  }

  private showMessage(severity: 'success' | 'info' | 'warn' | 'error', detail: string) {
    this.messageService.add({
      severity: severity,
      summary: severity.charAt(0).toUpperCase() + severity.slice(1),
      detail: detail
    });
  }

  nextStep() {
    if (this.currentStep < 6) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  private startEmailOTPTimer() {
    this.emailOTPTimer = 27;
    this.emailCanResend = false;

    this.emailOTPTimerInterval = setInterval(() => {
      this.emailOTPTimer--;
      if (this.emailOTPTimer <= 0) {
        clearInterval(this.emailOTPTimerInterval);
        this.emailCanResend = true;
      }
    }, 1000);
  }

  private startPhoneOTPTimer() {
    this.phoneOTPTimer = 27;
    this.phoneCanResend = false;

    this.phoneOTPTimerInterval = setInterval(() => {
      this.phoneOTPTimer--;
      if (this.phoneOTPTimer <= 0) {
        clearInterval(this.phoneOTPTimerInterval);
        this.phoneCanResend = true;
      }
    }, 1000);
  }

  private stopEmailOTPTimer() {
    if (this.emailOTPTimerInterval) {
      clearInterval(this.emailOTPTimerInterval);
      this.emailOTPTimerInterval = null;
    }
  }

  private stopPhoneOTPTimer() {
    if (this.phoneOTPTimerInterval) {
      clearInterval(this.phoneOTPTimerInterval);
      this.phoneOTPTimerInterval = null;
    }
  }

  resendEmailOTP(event?: Event) {
    if (event) event.preventDefault();
    if (!this.emailCanResend) return;

    this.isEmailLoading = true;
    const email = this.signupForm.get('email')?.value;

    this.authService.validateEmail(email).subscribe({
      next: (response: validateEmailResponse) => {
        this.isEmailLoading = false;
        this.showMessage('success', 'OTP resent to your email for verification.');
        this.startEmailOTPTimer();
      },
      error: (error) => {
        this.isEmailLoading = false;
        this.showMessage('error', 'Failed to resend OTP. Please try again.');
        console.error('Email OTP resend error:', error);
      }
    });
  }

  // Commented out phone OTP resend method - temporary until SMS gateway integration
  resendPhoneOTP(event?: Event) {
    if (event) event.preventDefault();
    if (!this.phoneCanResend) return;

    this.isPhoneLoading = true;
    const countryCode = this.signupForm.get('countryCode')?.value;
    const phoneNumber = this.signupForm.get('phoneNumber')?.value;
    const fullPhoneNumber = phoneNumber;

    const request: ValidatePhoneRequest = {
      phoneNumber: fullPhoneNumber
    };

    this.authService.validatePhone(request).subscribe({
      next: (response: ValidatePhoneResponse) => {
        this.isPhoneLoading = false;
        this.showMessage('success', 'OTP resent to your phone for verification.');
        this.startPhoneOTPTimer();
      },
      error: (error) => {
        this.isPhoneLoading = false;
        this.showMessage('error', 'Failed to resend OTP. Please try again.');
        console.error('Phone OTP resend error:', error);
      }
    });
  }

  ngOnDestroy() {
    this.stopEmailOTPTimer();
    this.stopPhoneOTPTimer();
  }
}
